import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { ContactEventsData, ContactsData, ExtraFilter, FilterTag, Page } from '../models/Contact';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  EditSearchPhraseAction,
  GetDataAction,
  ResetState,
  SetExcludedContactsAction,
  SetMassiveOptionAction,
  ToggleTagEditorAction,
  UpdateEditedFilterList
} from '../../contacts.actions';
import { Store } from '@ngrx/store';
import { IState } from '../../../shared/models/state';
import { TranslateService } from '@ngx-translate/core';
import { Msisdn } from '../../../shared/msisdn/msisdns';
import { ReportMetaData } from '../../../reports/report';
import * as moment from 'moment';

@Injectable()
export class ContactsService {
  public toggleOpeningFilters = new BehaviorSubject<boolean>(opener);
  public useFilters = new Subject();
  public toggleOpeningBasicFilters = new BehaviorSubject<boolean>(opener);
  public toggleOpeningSearchFilters = new BehaviorSubject<boolean>(opener);
  public toggleOpeningUniversalFilters = new BehaviorSubject<boolean>(opener);
  public toggleOpeningCallLengthsFilters = new BehaviorSubject<boolean>(opener);
  public toggleOpeningPhoneNumbersFilters = new BehaviorSubject<boolean>(opener);
  public onTagFilterOff = new BehaviorSubject<FilterTag>(opener);
  public onCallRangeTagFilterOff = new BehaviorSubject<FilterTag>(opener);
  public onPhoneNumberFilterOff = new BehaviorSubject<FilterTag>(opener);
  public onRangeChanged = new BehaviorSubject<{ dateFrom: string; dateTo: string }>(opener);
  public onOpenFilterToEdit = new BehaviorSubject<string>(opener);
  public onFilterExtraBy = new BehaviorSubject<ExtraFilter>(opener);
  public onFilterEditDone = new Subject();
  public onCallRangeTagCollectionOff = new Subject();
  public onSnapshotCallFinishType = new Subject();
  public toggleOpeningChangesNotifier = new BehaviorSubject<boolean>(opener);
  public onSetTagEditorPosition = new BehaviorSubject<{ rowId: number; top: number; left: number }>(opener);
  public onRowCollapse = new Subject();
  public onAddTagsToContacts = new BehaviorSubject<{ includedToTag?: number[]; excludedFromTag?: number[] }>(opener);
  public toggleShowTagsEditor = new BehaviorSubject<boolean>(opener);

  constructor(private http: HttpClient, private store: Store<IState>, public translate: TranslateService) {}

  getContactsData(filterParams): Observable<ContactsData> {
    return this.http.post<ContactsData>('/contacts-list', filterParams);
  }

  getDetails(filterParams: any): Observable<ContactEventsData> {
    return this.http.post<ContactEventsData>('/contacts-list/events', filterParams);
  }

  getOptionalData(eventsOption, filterParams) {
    if (!eventsOption) {
      return this.getContactsData(filterParams);
    } else {
      return this.getDetails(filterParams);
    }
  }

  fetchDataAction(pageParams: Page, offset = null) {
    const independentOffset = offset !== 0 ? pageParams.pagination.offset : offset;
    this.store.dispatch(new GetDataAction({ page: pageParams, offset: independentOffset }));
  }

  getPhoneNumbers(): Observable<Msisdn[]> {
    return this.http.get<Msisdn[]>('/record-events/historical-msisdns');
  }

  getMainStatuses(): Observable<string[]> {
    return this.http.get<string[]>('/record-events/record-statuses');
  }

  getUsers(): Observable<string[]> {
    return this.http.get<string[]>('/users');
  }

  getFilterByTags(): Observable<string[]> {
    return this.http.get<string[]>('/contacts-list/tags');
  }

  getEventsTypes(): Observable<string[]> {
    return this.http.get<string[]>('/record-events/event-statuses');
  }

  deleteContacts(filterParams): Observable<ContactsData> {
    return this.http.post<ContactsData>('/contacts-list/delete', filterParams);
  }

  setTagsForSelected(filterParams) {
    return this.http.post<ContactsData>('/contacts-list/tag', filterParams);
  }

  removeTagsForSelected(filterParams) {
    return this.http.post<ContactsData>('/contacts-list/untag', filterParams);
  }

  getPossibleMinDate(): Observable<ReportMetaData> {
    return this.http.get<ReportMetaData>(`/reports/overallMeta`);
  }

  showFilters() {
    this.toggleOpeningFilters.next(true);
  }

  hideFilters() {
    this.toggleOpeningFilters.next(false);
  }

  closeAllFilters() {
    this.hideFilters();
    this.hideSearchFilters();
    this.hideUniversalFilters();
    this.hidePhoneNumbersFilters();
    this.hideCallLengthsFilters();
  }

  showSearchFilters() {
    this.toggleOpeningSearchFilters.next(true);
    this.toggleOpeningChangesNotifier.next(false);
  }

  hideSearchFilters() {
    this.toggleOpeningSearchFilters.next(false);
  }

  showUniversalFilters() {
    this.toggleOpeningUniversalFilters.next(true);
  }

  hideUniversalFilters() {
    this.toggleOpeningUniversalFilters.next(false);
  }

  showCallLengthsFilters() {
    this.toggleOpeningCallLengthsFilters.next(true);
  }

  hideCallLengthsFilters() {
    this.toggleOpeningCallLengthsFilters.next(false);
  }

  showPhoneNumbersFilters() {
    this.toggleOpeningPhoneNumbersFilters.next(true);
  }

  hidePhoneNumbersFilters() {
    this.toggleOpeningPhoneNumbersFilters.next(false);
  }

  useNewFilters() {
    this.useFilters.next();
  }

  getProjectsList() {
    return this.http.get<any>('/projects/state');
  }

  onDoneFilter() {
    this.onFilterEditDone.next();
    this.store.dispatch(new UpdateEditedFilterList());
  }

  switchOffOtherFilter(tag: FilterTag) {
    this.onTagFilterOff.next(tag);
  }

  switchOffClientsPhoneNumberFilter(tag: FilterTag) {
    this.onPhoneNumberFilterOff.next(tag);
  }

  switchOffCallLengthFilter(tag: FilterTag) {
    this.onCallRangeTagFilterOff.next(tag);
  }

  onChangeRange(event) {
    this.onRangeChanged.next({ dateFrom: event.dateFrom, dateTo: event.dateTo });
  }

  openFilterToEdit(groupKey: string) {
    this.showFilters();
    this.onOpenFilterToEdit.next(groupKey);
  }

  getFilterTitle(filterName: string) {
    switch (filterName) {
      case 'callerIds':
        return 'CONTACTS_LIST.MY_PHONE_NUMBER';
      case 'clientPhoneNumbers':
        return 'CONTACTS_LIST.PHONE_NUMBER';
      case 'users':
        return 'CONTACTS_LIST.USER';
      case 'eventTypes':
        return 'CONTACTS_LIST.EVENT_TYPE';
      case 'callLengths':
        return 'CONTACTS_LIST.CALL_LENGTH';
      case 'tags':
        return 'CONTACTS_LIST.ADD_TAG';
    }
  }

  getEventTypeLabel(statusName: string) {
    switch (statusName) {
      case 'AllEvent':
        return `CONTACTS_LIST.STATUS.ALL`;
      case 'AutoscheduledEvent':
        return `CONTACTS_LIST.STATUS.AUTOSCHEDULED`;
      case 'ClosedEvent':
        return `CONTACTS_LIST.STATUS.CLOSED`;
      case 'RefuseEvent':
        return `CONTACTS_LIST.STATUS.REFUSE`;
      case 'ScheduleEvent':
        return `CONTACTS_LIST.STATUS.SCHEDULE`;
      case 'FailedContactEvent':
        return `CONTACTS_LIST.STATUS.FAILED_CONTACT`;
      case 'CloseContactEvent':
        return `CONTACTS_LIST.STATUS.CLOSE_CONTACT`;
      case 'MsisdnConfigChangeEvent':
        return `CONTACTS_LIST.STATUS.MSISDN_CONFIG_CHANGE`;
      case 'EmailConfigChangeEvent':
        return `CONTACTS_LIST.STATUS.EMAIL_CONFIG_CHANGE`;
      case 'IncomingCallEvent':
        return `CONTACTS_LIST.STATUS.INCOMING_CALL`;
      case 'IncomingCallAnsweredEvent':
        return `CONTACTS_LIST.STATUS.INCOMING_CALL_ANSWERED`;
      case 'IncomingCallRedirectedEvent':
        return `CONTACTS_LIST.STATUS.INCOMING_CALL_REDIRECTED`;
      case 'IncomingCallNotAnsweredEvent':
        return `CONTACTS_LIST.STATUS.INCOMING_CALL_NOT_ANSWERED`;
      case 'ConferenceCallEvent':
        return `CONTACTS_LIST.STATUS.CONFERENCE_CALL`;
      case 'TransferCallEvent':
        return `CONTACTS_LIST.STATUS.TRANSFER_CALL`;
      case 'VoiceDropEvent':
      case 'VoiceMailDropEvent':
        return `CONTACTS_LIST.STATUS.VOICE_MAIL_DROP`;
      case 'ImportContactEvent':
        return `CONTACTS_LIST.STATUS.IMPORT_CONTACT`;
      case 'ContactDetailsChangeEvent':
        return `CONTACTS_LIST.STATUS.CONTACT_DETAILS_CHANGE`;
      case 'DeleteContactEvent':
        return `CONTACTS_LIST.STATUS.DELETE_CONTACT`;
      case 'NoteEvent':
        return `CONTACTS_LIST.STATUS.NOTE`;
      case 'OpenContactEvent':
        return `CONTACTS_LIST.STATUS.OPEN_CONTACT`;
      case 'UnavailableMsisdn':
        return `CONTACTS_LIST.STATUS.UNAVAILABLE_MSISDN`;
    }
  }

  removeCallLengthCollection() {
    this.onCallRangeTagCollectionOff.next();
  }

  resetState() {
    this.store.dispatch(new ResetState());
  }

  resetSessionStorage() {
    window.sessionStorage.clear();
  }

  snapshotCallFinishType() {
    this.onSnapshotCallFinishType.next();
  }

  applyChangesNotifier(opener) {
    this.toggleOpeningChangesNotifier.next(opener);
  }

  rowCollapse() {
    this.onRowCollapse.next();
  }

  addParamsToContacts(params) {
    this.onAddTagsToContacts.next(params);
  }

  toggleTagsEditor(isTagEditorOpened) {
    this.store.dispatch(new ToggleTagEditorAction(isTagEditorOpened));
  }

  setTagEditorPosition(params: { top: number; left: number; rowId: number }) {
    this.onSetTagEditorPosition.next(params);
  }

  toggleShowRowTagsEditor(opener) {
    this.toggleShowTagsEditor.next(opener);
  }

  opaqueColumns(filteredColumns) {
    return filteredColumns.map(col => {
      const colName = col.type === 'NATIVE' ? `CONTACTS_LIST.${col.name}` : col.name;
      let colTranslation: string;
      this.translate.get(colName).subscribe(resp => {
        colTranslation = resp;
      });

      return {
        name: colName,
        type: col.type,
        key: col.key,
        frozenLeft: col.name === 'DETAILS' || col.name === 'NATIVE_EVENT_TYPE',
        columnName: col.type === 'NATIVE' ? colName : col.name,
        translation: col.type === 'NATIVE' ? colTranslation : col.name
      };
    });
  }

  searchByPhrase(phrase) {
    this.store.dispatch(new EditSearchPhraseAction(phrase));
  }

  filterExtraBy(extraFilter: ExtraFilter) {
    this.onFilterExtraBy.next(extraFilter);
  }

  clearMassiveActions(massiveActionOption, excludedContacts: number[]) {
    this.store.dispatch(new SetMassiveOptionAction(massiveActionOption));
    this.store.dispatch(new SetExcludedContactsAction(excludedContacts));
  }

  calculateMinDateByLimit(limit: number, utcOffset: number) {
    return limit
      ? moment().subtract(limit, 'days')
      : moment('1978-07-16')
  }
}
